// /settings → "Амралтын календарь" tab — create/edit modal (A4 §2.7a item 4).
// Fields mirror the backend Joi schema: holidayDate (required), name (required),
// description (optional), isFullDay (boolean), recurrence (ONE_TIME | YEARLY).

import {
  Checkbox,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { CalendarIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import type { IBusinessHoliday, ICreateHolidayInput, HolidayRecurrence } from "../../apis/holiday-calendar"
import { useSettingsMutations } from "./hooks"
import { SettingsModal } from "./settings-modal"

type FormShape = {
  holidayDate: Date | null
  name: string
  description: string
  isFullDay: boolean
  recurrence: HolidayRecurrence
}

type Props = {
  opened: boolean
  onClose: () => void
  initial?: IBusinessHoliday | null
  onSaved?: () => void
}

function fromInitial(r?: IBusinessHoliday | null): FormShape {
  return {
    holidayDate: r?.holidayDate ? new Date(r.holidayDate) : null,
    name       : r?.name ?? "",
    description: r?.description ?? "",
    isFullDay  : r?.isFullDay ?? true,
    recurrence : r?.recurrence ?? "ONE_TIME",
  }
}

export function HolidayFormModal({ opened, onClose, initial, onSaved }: Props) {
  const { t } = useTranslation("settings")
  const mutations = useSettingsMutations()
  const isEdit = !!initial

  const recurrenceOptions: { value: HolidayRecurrence; label: string }[] = [
    { value: "ONE_TIME", label: t("holiday.holidays.options.ONE_TIME") },
    { value: "YEARLY",  label: t("holiday.holidays.options.YEARLY") },
  ]

  const form = useForm<FormShape>({
    initialValues: fromInitial(initial),
    validate: {
      holidayDate: (v) => (!v ? t("holiday.holidays.validation.required", { field: t("holiday.holidays.fields.holidayDate.label") }) : null),
      name       : (v) => (!v.trim() ? t("holiday.holidays.validation.required", { field: t("holiday.holidays.fields.name.label") }) : null),
      description: (v) =>
        v && v.length > 500 ? t("holiday.holidays.validation.descTooLong") : null,
    },
  })

  const handleSubmit = async (values: FormShape) => {
    if (!values.holidayDate) return

    // Format Date to YYYY-MM-DD for the backend (DATEONLY field).
    const pad = (n: number) => String(n).padStart(2, "0")
    const d = values.holidayDate
    const isoDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

    const payload: ICreateHolidayInput = {
      holidayDate: isoDate,
      name       : values.name.trim(),
      description: values.description.trim() || null,
      isFullDay  : values.isFullDay,
      recurrence : values.recurrence,
    }

    if (isEdit && initial) {
      await mutations.holiday.update(initial.id, payload)
    } else {
      await mutations.holiday.create(payload)
    }

    onSaved?.()
    onClose()
    form.reset()
  }

  return (
    <SettingsModal
      opened={opened}
      onClose={onClose}
      title={isEdit
        ? t("holiday.holidays.modal.editTitle")
        : t("holiday.holidays.modal.createTitle")}
      onSubmit={form.onSubmit(handleSubmit)}
      submitLabel={isEdit ? t("common.save") : t("common.create")}
      submitting={mutations.submitting}
    >
          <DatePickerInput
            label={t("holiday.holidays.fields.holidayDate.label")}
            required
            leftSection={<CalendarIcon size={16} />}
            valueFormat="YYYY-MM-DD"
            {...form.getInputProps("holidayDate")}
          />

          <TextInput
            label={t("holiday.holidays.fields.name.label")}
            required
            placeholder={t("holiday.holidays.fields.name.placeholder")}
            {...form.getInputProps("name")}
          />

          <Textarea
            label={t("holiday.holidays.fields.description.label")}
            autosize
            minRows={2}
            maxRows={4}
            placeholder={t("holiday.holidays.fields.description.placeholder")}
            {...form.getInputProps("description")}
          />

          <Select
            label={t("holiday.holidays.fields.recurrence.label")}
            data={recurrenceOptions}
            {...form.getInputProps("recurrence")}
          />

          <Checkbox
            label={t("holiday.holidays.fields.isFullDay.label")}
            checked={form.values.isFullDay}
            onChange={(e) => form.setFieldValue("isFullDay", e.currentTarget.checked)}
          />
    </SettingsModal>
  )
}
